import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged, User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { db, auth, functions, storage } from '../lib/firebase';
import { ensureAnonUser, linkAnonToGoogle } from '../lib/anon';
import { stripGpsExif } from '../lib/exif';
import { makeThumbnail, compressForUpload, placeholderThumb } from '../lib/thumbnail';
import { applyFrame } from '../lib/frameOverlay';
import { makeVideoPoster } from '../lib/videoPoster';
import { enqueue, listAll, subscribe, QueueItem } from '../lib/queue';
import { fire } from '../lib/analytics';
import { isStandalone } from '../lib/useInstallPrompt';

interface EventDoc {
  title?: string;
  slug: string;
  coupleUid?: string;
  status?: 'pending' | 'active' | 'rejected';
  pausedByHost?: boolean;
  uploadCount?: number;
  galleryVisibleToGuests?: boolean;
  moderationMode?: 'open' | 'approvalRequired';
  themeConfig?: {
    primary?: string;
    accent?: string;
    background?: string;
    couple?: string;
    fontPair?: string;
  };
  frameUrl?: string | null;
  bannerPath?: string | null;
  disposableMode?: {
    enabled?: boolean;
    revealAt?: unknown;
    shotsPerGuest?: number;
  };
}

function isDarkHex(hex: string): boolean {
  const m = /^#?([a-f\d]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 0xff;
  const g = (int >> 8) & 0xff;
  const b = int & 0xff;
  // Perceived brightness — anything below ~140 reads as "dark background".
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum < 140;
}

function toMillis(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : t;
  }
  const asTs = v as { toMillis?: () => number; seconds?: number };
  if (typeof asTs.toMillis === 'function') return asTs.toMillis();
  if (typeof asTs.seconds === 'number') return asTs.seconds * 1000;
  return null;
}

export function GuestPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const qrToken = searchParams.get('t') ?? undefined;

  const [event, setEvent] = useState<{ eventId: string; data: EventDoc } | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nickname, setNickname] = useState('');
  const [items, setItems] = useState<QueueItem[]>([]);
  const [ready, setReady] = useState(false);
  const [shotsRemaining, setShotsRemaining] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [nameEditorOpen, setNameEditorOpen] = useState(false);
  const [toast, setToast] = useState<{ id: number; count: number; milestone: boolean } | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  // Track the last-seen complete count so we can fire a toast on transitions
  // (rather than every render).
  const lastSentRef = useRef(0);

  const [standalone, setStandalone] = useState(false);
  useEffect(() => setStandalone(isStandalone()), []);

  useEffect(() => onAuthStateChanged(auth, setUser), []);

  // Persist the guest's nickname per-event so a page reload doesn't drop it.
  // Key includes slug so different events keep their own name.
  const nicknameKey = slug ? `kept:nickname:${slug}` : null;
  useEffect(() => {
    if (!nicknameKey) return;
    const stored = localStorage.getItem(nicknameKey);
    if (stored) setNickname(stored);
  }, [nicknameKey]);
  useEffect(() => {
    if (!nicknameKey) return;
    if (nickname.trim()) localStorage.setItem(nicknameKey, nickname.trim());
  }, [nickname, nicknameKey]);

  useEffect(() => {
    (async () => {
      try {
        await ensureAnonUser();
        setReady(true);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, []);

  useEffect(() => {
    fire('page_loaded');
    if (qrToken) fire('qr_scanned', { tableId: qrToken });
  }, [qrToken]);

  useEffect(() => {
    if (!slug) return;
    let unsub: (() => void) | null = null;
    (async () => {
      try {
        const slugSnap = await getDoc(doc(db, `eventSlugs/${slug}`));
        if (!slugSnap.exists()) {
          setError("This link doesn't look right. Ask the host for a fresh one.");
          return;
        }
        const eventId = slugSnap.get('eventId') as string;
        // Live subscribe — uploadCount ticks up as other guests share, and
        // pausedByHost / status flip mid-session should re-render.
        unsub = onSnapshot(
          doc(db, `events/${eventId}`),
          (snap) => {
            if (!snap.exists()) {
              setError('Event missing. Ask the host to check the link.');
              return;
            }
            const data = snap.data() as EventDoc;
            setEvent({ eventId, data });
          },
          (e) => setError((e as Error).message)
        );
        fire('page_loaded', { eventId, tableId: qrToken ?? '' });
        if (qrToken) fire('qr_scanned', { eventId, tableId: qrToken });
      } catch (e) {
        setError((e as Error).message);
      }
    })();
    return () => {
      if (unsub) unsub();
    };
  }, [slug, qrToken]);

  // Resolve the banner's download URL when the event has one.
  useEffect(() => {
    const path = event?.data.bannerPath;
    if (!path) {
      setBannerUrl(null);
      return;
    }
    getDownloadURL(storageRef(storage, path))
      .then(setBannerUrl)
      .catch(() => setBannerUrl(null));
  }, [event?.data.bannerPath]);

  useEffect(() => {
    if (!ready || !event || !auth.currentUser) return;
    (async () => {
      try {
        const ref = doc(db, `events/${event.eventId}/guests/${auth.currentUser!.uid}`);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          await setDoc(ref, {
            createdAt: serverTimestamp(),
            qrScannedAt: Date.now(),
            userAgent: navigator.userAgent.slice(0, 500),
            shotsRemaining: event.data.disposableMode?.enabled
              ? event.data.disposableMode?.shotsPerGuest ?? 24
              : null,
          });
          setShotsRemaining(
            event.data.disposableMode?.enabled ? event.data.disposableMode?.shotsPerGuest ?? 24 : null
          );
        } else {
          const existing = snap.data() as { shotsRemaining?: number };
          setShotsRemaining(
            event.data.disposableMode?.enabled ? existing.shotsRemaining ?? null : null
          );
        }
      } catch {
        // ignore
      }
    })();
  }, [ready, event]);

  useEffect(() => {
    let alive = true;
    const refresh = () => listAll().then((all) => alive && setItems(all));
    refresh();
    const unsub = subscribe(refresh);
    return () => {
      alive = false;
      unsub();
    };
  }, []);

  const relevant = useMemo(
    () => items.filter((i) => i.eventId === event?.eventId),
    [items, event]
  );

  const disposableEnabled = event?.data.disposableMode?.enabled === true;
  const revealMs = event ? toMillis(event.data.disposableMode?.revealAt) : null;
  const revealed = !disposableEnabled || (revealMs != null && Date.now() >= revealMs);

  async function handleFiles(files: FileList | null) {
    if (!files || !event) return;
    // Name is optional. Signed-in guests inherit from Google; anonymous guests
    // may have typed a nickname or left it blank. Fallback: undefined → the
    // upload just carries no uploaderName. Couple sees a generic marker.
    const currentName = auth.currentUser?.isAnonymous === false
      ? auth.currentUser.displayName ?? auth.currentUser.email ?? ''
      : nickname.trim();
    if (disposableEnabled && shotsRemaining !== null && shotsRemaining <= 0) return;
    setProcessing(true);
    setPickError(null);
    let remaining = shotsRemaining;
    const failures: Array<{ name: string; reason: string }> = [];
    const rejected: string[] = [];
    try {
      for (const file of Array.from(files)) {
        if (disposableEnabled && remaining !== null && remaining <= 0) break;
        // Strict allow-list, magic-byte-sniffed. iOS lies about MIME types
        // for HEIC/HEIF files, so trusting file.type here loses photos.
        const kind = await detectImageKind(file);
        if (!kind) {
          rejected.push(file.name || 'file');
          continue;
        }
        fire('file_selected', { eventId: event.eventId, tableId: qrToken ?? '' });
        try {
          let uploadBlob: Blob;
          let uploadMime: string;
          let thumbBlob: Blob;

          if (isVideoKind(kind)) {
            // Videos upload as-is (no client-side transcode). Poster frame
            // becomes the thumbnail so the tile has something to show.
            uploadBlob = file;
            uploadMime = kind;
            thumbBlob = await makeVideoPoster(file);
          } else if (isHeifKind(kind)) {
            // HEIC/HEIF: browsers can't decode these to canvas, so we skip
            // GPS strip (JPEG-only anyway), frame overlay, and compression.
            // Upload the raw bytes with the correct MIME so Drive shows the
            // right thumbnail. Guest tile gets a placeholder swatch.
            uploadBlob = file;
            uploadMime = kind;
            thumbBlob = await placeholderThumb();
          } else {
            // JPEG/PNG path — GPS strip (JPEG only), compress, frame overlay,
            // thumbnail. Compress first so the remaining canvas steps run on
            // a smaller working blob.
            let cleaned: File = file;
            if (kind === 'image/jpeg') {
              try {
                const cleanBuf = stripGpsExif(await file.arrayBuffer());
                cleaned = new File([cleanBuf], file.name, { type: kind });
              } catch {
                cleaned = file;
              }
            }
            const compressed = await compressForUpload(cleaned);
            const compressedFile = compressed instanceof File
              ? compressed
              : new File([compressed], file.name, { type: 'image/jpeg' });
            const composited = await applyFrame(compressedFile, event.data.frameUrl ?? null);
            const thumb = await makeThumbnail(
              composited instanceof File ? composited : new File([composited], file.name, { type: 'image/jpeg' })
            );
            uploadBlob = composited;
            uploadMime = composited === file ? kind : 'image/jpeg';
            thumbBlob = thumb.blob;
          }

          const id = crypto.randomUUID();
          await enqueue({
            id,
            eventId: event.eventId,
            slug: slug!,
            filename: file.name,
            mimeType: uploadMime,
            size: uploadBlob.size,
            blob: uploadBlob,
            thumbBlob,
            status: 'pending',
            attempts: 0,
            uploaderName: currentName || undefined,
            tableId: qrToken,
            createdAt: Date.now(),
          });
          if (disposableEnabled && remaining !== null) {
            remaining -= 1;
            try {
              if (auth.currentUser) {
                const ref = doc(db, `events/${event.eventId}/guests/${auth.currentUser.uid}`);
                await updateDoc(ref, { shotsRemaining: remaining });
              }
            } catch {}
          }
        } catch (err) {
          // One bad file shouldn't kill the batch. Surface at end with a
          // real error string so we can debug rather than a generic message.
          const reason = err instanceof Error ? err.message : String(err);
          failures.push({ name: file.name || 'photo', reason });
          // eslint-disable-next-line no-console
          console.warn('Failed to prepare file for upload', file.name, err);
        }
      }
      if (disposableEnabled && remaining !== null) setShotsRemaining(remaining);
      const problems: string[] = [];
      if (rejected.length) {
        problems.push(
          rejected.length === 1
            ? `"${rejected[0]}" isn't supported. We accept HEIC, JPG, PNG, MOV, or MP4.`
            : `${rejected.length} files skipped — we accept HEIC, JPG, PNG, MOV, or MP4 only.`
        );
      }
      if (failures.length) {
        // Show the exact underlying reason so we can diagnose quickly.
        const first = failures[0];
        problems.push(
          failures.length === 1
            ? `Couldn't read "${first.name}" — ${first.reason.slice(0, 100)}`
            : `Couldn't read ${failures.length} photos — first: ${first.reason.slice(0, 80)}`
        );
      }
      if (problems.length) setPickError(problems.join(' '));
    } finally {
      setProcessing(false);
    }
  }

  // Match against the strict allow-list. iOS lies: the photo picker will
  // often report `image/jpeg` for HEIC/HEIF files that are actually HEIF bytes
  // under the hood. We sniff the leading magic bytes to correct that, so the
  // upload carries a MIME type Drive can preview properly.
  async function detectImageKind(file: File): Promise<
    | 'image/heic'
    | 'image/heif'
    | 'image/jpeg'
    | 'image/png'
    | 'video/quicktime'
    | 'video/mp4'
    | null
  > {
    // Magic-byte sniff first — the only source of truth.
    try {
      const head = new Uint8Array(await file.slice(0, 32).arrayBuffer());
      // JPEG: FF D8 FF
      if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return 'image/jpeg';
      // PNG: 89 50 4E 47 0D 0A 1A 0A
      if (head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47) return 'image/png';
      // ISO Base Media (HEIC/HEIF/MP4/MOV): bytes 4-7 = "ftyp"
      if (head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70) {
        const brand = String.fromCharCode(head[8], head[9], head[10], head[11]);
        // HEIC: heic, heix, hevc, hevx | HEIF: mif1, msf1 | HEIC image sequence: hevc
        if (['heic', 'heix', 'hevc', 'hevx'].includes(brand)) return 'image/heic';
        if (['mif1', 'msf1'].includes(brand)) return 'image/heif';
        // MP4: isom, mp41, mp42, avc1, dash | MOV: qt
        if (['isom', 'mp41', 'mp42', 'avc1', 'dash', 'iso2', 'iso4', 'iso5'].includes(brand)) return 'video/mp4';
        if (brand === 'qt  ') return 'video/quicktime';
      }
    } catch {
      // Fall through to MIME/extension detection.
    }
    const t = (file.type || '').toLowerCase();
    if (t === 'image/heic') return 'image/heic';
    if (t === 'image/heif') return 'image/heif';
    if (t === 'image/jpeg' || t === 'image/jpg') return 'image/jpeg';
    if (t === 'image/png') return 'image/png';
    if (t === 'video/quicktime' || t === 'video/mov') return 'video/quicktime';
    if (t === 'video/mp4') return 'video/mp4';
    const name = (file.name || '').toLowerCase();
    if (name.endsWith('.heic')) return 'image/heic';
    if (name.endsWith('.heif')) return 'image/heif';
    if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
    if (name.endsWith('.png')) return 'image/png';
    if (name.endsWith('.mov')) return 'video/quicktime';
    if (name.endsWith('.mp4') || name.endsWith('.m4v')) return 'video/mp4';
    return null;
  }

  function isVideoKind(kind: string): boolean {
    return kind === 'video/quicktime' || kind === 'video/mp4';
  }

  function isHeifKind(kind: string): boolean {
    return kind === 'image/heic' || kind === 'image/heif';
  }

  // Toast: fire whenever a new upload actually completes. Replaces any prior
  // toast so quick bursts don't stack. Timer clears itself when this component
  // unmounts. Skipped if the guest hasn't given us a name yet.
  //
  // MUST live above the early returns below — moving it below would let the
  // hook count change between renders (React Rules of Hooks) and blank the page.
  useEffect(() => {
    if (!event) return;
    const eid = event.eventId;
    const doneCount = items.filter((i) => i.eventId === eid && i.status === 'complete').length;
    const name = user && !user.isAnonymous
      ? user.displayName ?? user.email ?? ''
      : nickname.trim();
    if (doneCount > lastSentRef.current) {
      lastSentRef.current = doneCount;
      if (name) {
        const milestone = [5, 10, 25, 50, 100].includes(doneCount);
        const id = Date.now();
        setToast({ id, count: doneCount, milestone });
        const t = window.setTimeout(() => {
          setToast((cur) => (cur?.id === id ? null : cur));
        }, milestone ? 3500 : 2500);
        return () => window.clearTimeout(t);
      }
    } else if (doneCount < lastSentRef.current) {
      lastSentRef.current = doneCount;
    }
  }, [items, event, user, nickname]);

  // ------- rendering -------

  if (error) return <ErrorScreen msg={error} />;
  if (!ready || !event) return <LoadingScreen />;

  // Verification gate. Legacy events without a status field are treated as
  // active so pre-existing production events keep working.
  const eventStatus = event.data.status ?? 'active';
  if (eventStatus === 'pending') return <PendingScreen title={event.data.title} />;
  if (eventStatus === 'rejected') {
    return <ErrorScreen msg="This event is no longer available. If you think that's a mistake, reach out to the host." />;
  }
  // Host-controlled pause. Softer than pending/rejected — implies "come back later".
  if (event.data.pausedByHost === true) return <PausedScreen title={event.data.title} />;

  const theme = event.data.themeConfig ?? {};
  const primary = theme.primary ?? '#c47a52';
  const background = theme.background ?? '#faf7f2';
  const subtitle = theme.couple;
  const title = event.data.title ?? event.data.slug;

  // Rough luminance check on the background so the sticky-header text uses
  // the same primary color but with adequate contrast against dark bgs.
  const bgIsDark = isDarkHex(background);

  const styleVars: React.CSSProperties = {
    ['--kp-primary' as string]: primary,
    backgroundColor: background,
    ...(theme.fontPair ? { fontFamily: theme.fontPair } : {}),
  };

  const sent = relevant.filter((i) => i.status === 'complete').length;
  const inFlight = relevant.filter((i) => i.status === 'pending' || i.status === 'uploading').length;
  const failed = relevant.filter((i) => i.status === 'failed').length;
  const hasSent = sent > 0;
  const isMilestone = sent > 0 && [5, 10, 25, 50, 100].includes(sent);
  const isRealUser = !!user && !user.isAnonymous;
  // A guest gets their name from their Google profile once signed in.
  // Anonymous guests must type a nickname before they can pick photos.
  const trimmedNickname = nickname.trim();
  const effectiveName = isRealUser
    ? user?.displayName ?? user?.email ?? ''
    : trimmedNickname;


  async function handleSignIn() {
    setSigningIn(true);
    try {
      await linkAnonToGoogle();
    } catch (e) {
      console.error('sign-in failed', e);
    } finally {
      setSigningIn(false);
    }
  }

  return (
    <div
      className={`min-h-full flex flex-col overflow-x-hidden ${bgIsDark ? 'kp-canvas-dark' : ''}`}
      style={styleVars}
    >
      {/* Dark-canvas overrides — kick in when the couple picks a dark bg.
          Rules are scoped under .kp-canvas-dark so they never touch the
          light-mode default. Idea:
          - Text colors that were designed against cream get white tints.
          - Cards & interactive surfaces that were stone-100/stone-50 become
            translucent-white so they still stand out against the canvas
            without looking like grey blobs.
          - Borders switch from stone-100 to white/10 for consistency.
          - Input fields become slightly translucent white with dark placeholder
            so they don't glare. */}
      {bgIsDark && (
        <style>{`
          .kp-canvas-dark { color: rgba(255,255,255,0.85); }
          .kp-canvas-dark .text-stone-300 { color: rgba(255,255,255,0.42); }
          .kp-canvas-dark .text-stone-400 { color: rgba(255,255,255,0.6); }
          .kp-canvas-dark .text-stone-500 { color: rgba(255,255,255,0.72); }
          .kp-canvas-dark .text-stone-600 { color: rgba(255,255,255,0.78); }
          .kp-canvas-dark .text-stone-700 { color: rgba(255,255,255,0.86); }
          .kp-canvas-dark .text-stone-800 { color: rgba(255,255,255,0.92); }
          .kp-canvas-dark .placeholder\\:text-stone-300::placeholder { color: rgba(255,255,255,0.35); }

          /* Cards + surfaces */
          .kp-canvas-dark .bg-white { background-color: rgba(255,255,255,0.06); backdrop-filter: blur(4px); }
          .kp-canvas-dark .bg-stone-50 { background-color: rgba(255,255,255,0.04); }
          .kp-canvas-dark .bg-stone-100 { background-color: rgba(255,255,255,0.08); }
          .kp-canvas-dark .bg-stone-100\\/80 { background-color: rgba(255,255,255,0.08); }

          /* Borders */
          .kp-canvas-dark .border-stone-100 { border-color: rgba(255,255,255,0.08); }
          .kp-canvas-dark .border-stone-200 { border-color: rgba(255,255,255,0.12); }

          /* Shadows on dark bgs read as smudges — flatten them. */
          .kp-canvas-dark .shadow-card { box-shadow: 0 0 0 1px rgba(255,255,255,0.06); }
          .kp-canvas-dark .shadow-card-hover:hover { box-shadow: 0 0 0 1px rgba(255,255,255,0.14); }

          /* Sticky header title uses the theme primary; on a dark bg that
             color often becomes invisible. Force white for legibility. */
          .kp-canvas-dark header h1 { color: rgba(255,255,255,0.95) !important; }
        `}</style>
      )}
      {/* Installed-app mini bar. When guests add the page to their home
          screen, iOS/Android strip browser chrome — no back button, no
          reload. Give them ours. */}
      {standalone && (
        <div
          className={`sticky top-0 z-30 backdrop-blur border-b ${bgIsDark ? 'border-white/10' : 'border-stone-100'}`}
          style={{
            backgroundColor: `${background}f2`,
            paddingTop: 'env(safe-area-inset-top)',
          }}
        >
          <div className="max-w-3xl mx-auto px-3 h-11 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                if (window.history.length > 1) window.history.back();
                else window.location.assign('/');
              }}
              className={`p-2 rounded-full ${bgIsDark ? 'text-white/80 hover:bg-white/10' : 'text-stone-600 hover:bg-stone-100'} transition`}
              aria-label="Back"
            >
              <BackArrowIcon />
            </button>
            <span
              className={`text-[0.6rem] uppercase tracking-[0.2em] ${bgIsDark ? 'text-white/50' : 'text-stone-400'}`}
            >
              KeptShots
            </span>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={`p-2 rounded-full ${bgIsDark ? 'text-white/80 hover:bg-white/10' : 'text-stone-600 hover:bg-stone-100'} transition`}
              aria-label="Refresh"
            >
              <RefreshIcon />
            </button>
          </div>
        </div>
      )}
      {/* Sticky header — collapses to a slim bar when user is uploading */}
      <header
        className={`sticky ${standalone ? 'top-11' : 'top-0'} z-20 backdrop-blur border-b ${bgIsDark ? 'border-white/10' : 'border-stone-100'}`}
        style={{ backgroundColor: `${background}dd` }}
      >
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className={`text-[0.6rem] uppercase tracking-[0.24em] ${bgIsDark ? 'text-white/60' : 'text-stone-400'}`}>Photos for</p>
            <h1
              className="font-serif text-xl sm:text-2xl leading-tight truncate"
              style={{ color: primary }}
            >
              {title}
            </h1>
          </div>
          {isRealUser ? (
            <div className="flex items-center gap-2 shrink-0">
              {user?.photoURL ? (
                <img src={user.photoURL} alt="" className="w-8 h-8 rounded-full ring-1 ring-stone-200" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-stone-700 text-cream text-xs font-medium grid place-items-center">
                  {(user?.displayName ?? user?.email ?? '?')[0]?.toUpperCase()}
                </div>
              )}
              <span className="text-xs text-stone-500 hidden sm:inline max-w-[9rem] truncate">
                {user?.displayName ?? user?.email}
              </span>
            </div>
          ) : (
            <span className="text-[0.65rem] uppercase tracking-[0.18em] text-stone-400 shrink-0">
              Guest
            </span>
          )}
        </div>
      </header>

      {/* Clean hero banner — image only, no overlay text. Always visible
          throughout the flow so the host's chosen visual stays present. */}
      {bannerUrl && (
        <section className="w-full max-w-3xl mx-auto px-4 pt-4 animate-fade-in">
          <img
            src={bannerUrl}
            alt=""
            className="w-full aspect-[3/1] object-cover rounded-2xl shadow-card"
            loading="eager"
          />
        </section>
      )}

      <main className="flex-1 px-4 pt-6 pb-40 max-w-3xl mx-auto w-full space-y-5 animate-fade-in">
        {/* Title block — shown in the same text-only format regardless of
            whether a banner is set. Uses the event's title when a banner
            provided the visual context, otherwise the generic "Share your
            photos" copy. */}
        {!hasSent && (
          <section className="text-center space-y-2 pt-4 pb-2">
            <h2 className="font-serif text-xl sm:text-5xl leading-tight" style={{ color: primary }}>
              {bannerUrl ? title : 'Share your photos'}
            </h2>
            {subtitle && <p className="italic text-stone-500 font-serif text-xs sm:text-lg">{subtitle}</p>}
            <p className="text-stone-500 text-[0.7rem] sm:text-sm max-w-md mx-auto pt-1 leading-relaxed">
              Pick from your camera roll or shoot new ones — they land straight in the host's Drive.
            </p>
          </section>
        )}

        {/* Live counter — nudges the next guest to add theirs. Fed by
            Firestore onSnapshot on the event doc, so it ticks as other
            guests finalize uploads. Hidden until at least one photo. */}
        <LiveCounter count={event.data.uploadCount ?? 0} primary={primary} />


        {/* Nickname — OPTIONAL for anonymous guests; auto-filled for signed-in users.
            Guests can skip and upload as "Guest"; a soft nudge sits above the
            drop zone so they can add a name whenever they feel like it. */}
        {!hasSent ? (
          isRealUser ? (
            <div className="bg-white rounded-2xl shadow-card px-4 py-3 text-sm text-stone-500 flex items-center gap-2">
              <span>Sharing as</span>
              <span className="font-medium text-stone-800">{effectiveName}</span>
            </div>
          ) : (
            <div>
              <label className="block text-[0.6rem] sm:text-xs uppercase tracking-[0.2em] text-stone-400 mb-1.5 px-1">
                Your name <span className="text-stone-400 normal-case tracking-normal">(optional)</span>
              </label>
              <div className="bg-white rounded-2xl shadow-card p-1">
                <input
                  type="text"
                  placeholder="Type your name for the host"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="w-full bg-transparent px-3 sm:px-4 py-2.5 sm:py-3.5 text-sm sm:text-base focus:outline-none placeholder:text-stone-300"
                  maxLength={40}
                />
              </div>
              <p className="text-[0.65rem] sm:text-xs text-stone-400 mt-1.5 px-1">
                Skip it and you'll show up as "Guest" — no worries.
              </p>
            </div>
          )
        ) : (
          <div className="flex items-center justify-between gap-3 text-xs text-stone-400 px-2">
            <span className="flex items-center gap-2 flex-wrap">
              <span>
                Sharing as{' '}
                <span className="text-stone-700 font-medium">{effectiveName || 'Guest'}</span>
              </span>
              {hasSent && (
                <span className="text-stone-400">
                  · {isMilestone && <span className="mr-0.5">🎉</span>}
                  You've shared{' '}
                  <span
                    className={`font-medium tabular-nums transition ${isMilestone ? 'animate-pulse' : 'text-stone-700'}`}
                    style={isMilestone ? { color: primary } : undefined}
                  >
                    {sent}
                  </span>{' '}
                  {sent === 1 ? 'photo' : 'photos'}
                </span>
              )}
            </span>
            {!isRealUser && (
              <button
                onClick={() => setNameEditorOpen(true)}
                className="underline underline-offset-2 hover:text-stone-700 transition"
              >
                change
              </button>
            )}
          </div>
        )}

        {/* Disposable mode banner */}
        {disposableEnabled && (
          <div
            className="rounded-2xl bg-white shadow-card p-4 text-sm border-l-4"
            style={{ borderLeftColor: primary }}
          >
            <div className="font-medium mb-0.5" style={{ color: primary }}>Disposable camera mode</div>
            <div className="text-stone-500">
              {shotsRemaining !== null
                ? `You have ${shotsRemaining} shot${shotsRemaining === 1 ? '' : 's'} left.`
                : 'Limited shots per guest.'}
              {!revealed && revealMs && (
                <> The gallery reveals {new Date(revealMs).toLocaleString()}.</>
              )}
            </div>
          </div>
        )}

        {/* Drop zone — shrinks after first upload. Always active; name is optional. */}
        <label
          className={`block w-full rounded-3xl bg-white shadow-card text-center transition-all border-2 border-dashed cursor-pointer hover:shadow-card-hover group ${
            hasSent ? 'p-4 sm:p-6' : 'p-6 sm:p-10'
          }`}
          style={{
            borderColor: `${primary}55`,
            opacity: disposableEnabled && shotsRemaining === 0 ? 0.5 : 1,
          }}
        >
          <input
            type="file"
            accept="image/heic,image/heif,image/jpeg,image/png,video/quicktime,video/mp4,.heic,.heif,.jpg,.jpeg,.png,.mov,.mp4,.m4v"
            multiple
            {...(disposableEnabled ? { capture: 'environment' as const } : {})}
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            disabled={
              (disposableEnabled && shotsRemaining === 0) || processing
            }
          />
          <div
            className={`mx-auto rounded-2xl grid place-items-center mb-3 transition-transform group-hover:scale-105 ${hasSent ? 'w-9 h-9 sm:w-11 sm:h-11' : 'w-11 h-11 sm:w-14 sm:h-14'}`}
            style={{ background: `${primary}18`, color: primary }}
          >
            {processing ? <Spinner /> : <CameraIcon />}
          </div>
          <div className={`font-medium ${hasSent ? 'text-xs sm:text-base' : 'text-sm sm:text-lg'}`} style={{ color: primary }}>
            {processing
              ? 'Preparing…'
              : hasSent
              ? 'Add more photos'
              : 'Tap to add photos'}
          </div>
          {!hasSent && (
            <p className="text-[0.7rem] sm:text-sm text-stone-400 mt-1.5 max-w-xs mx-auto">
              They send in the background — even if you lose signal.
            </p>
          )}
        </label>

        {pickError && (
          <div className="rounded-2xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700 flex items-start gap-3">
            <span className="flex-1">{pickError}</span>
            <button
              type="button"
              onClick={() => setPickError(null)}
              className="text-red-500 hover:text-red-700 text-lg leading-none px-1"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Offline banner */}
        {!navigator.onLine && (
          <div className="rounded-2xl bg-amber-50 text-amber-800 text-sm p-4 border border-amber-100">
            You're offline. Photos are saved and will send when you're back on.
          </div>
        )}

        {/* Progress strip when items pending */}
        {relevant.length > 0 && (
          <section className="space-y-3">
            <div className="flex items-baseline justify-between px-1">
              <h2 className="font-medium text-stone-700">Your photos</h2>
              <p className="text-xs text-stone-400 tabular-nums">
                {sent > 0 && `${sent} shared`}
                {sent > 0 && inFlight > 0 && ' · '}
                {inFlight > 0 && `${inFlight} in the air`}
                {failed > 0 && ` · ${failed} failed`}
              </p>
            </div>
            <ul className="space-y-2">
              {relevant.map((i) => (
                <UploadTile key={i.id} item={i} accent={primary} />
              ))}
            </ul>
          </section>
        )}

        {/* Sign-in invitation — after first successful upload, anonymous only.
            Only shown when the host has opened the shared gallery to guests,
            otherwise signing in wouldn't reveal anything new. */}
        {hasSent && !isRealUser && revealed && event?.data.galleryVisibleToGuests === true && (
          <section
            className="rounded-3xl p-6 sm:p-7 text-center shadow-card animate-fade-in"
            style={{
              background: `linear-gradient(135deg, ${primary}12 0%, ${primary}05 100%)`,
              borderLeft: `4px solid ${primary}`,
            }}
          >
            <div className="max-w-md mx-auto space-y-3">
              <p className="text-[0.6rem] uppercase tracking-[0.24em]" style={{ color: primary }}>
                Optional
              </p>
              <h3 className="font-serif text-2xl text-stone-800 leading-tight">
                See everyone else's photos
              </h3>
              <p className="text-sm text-stone-500 leading-relaxed">
                Sign in with Google and we'll unlock the live gallery of every photo shared at this event.
              </p>
              <button
                onClick={handleSignIn}
                disabled={signingIn}
                className="inline-flex items-center gap-2.5 px-5 py-3 rounded-full bg-white shadow-card hover:shadow-card-hover transition text-stone-800 font-medium disabled:opacity-50"
              >
                <GoogleGlyph />
                {signingIn ? 'Opening…' : 'Sign in to see gallery'}
              </button>
              <p className="text-[0.65rem] text-stone-400 pt-1">
                We only read your name and email. Your photos stay yours.
              </p>
            </div>
          </section>
        )}

        {/* Public gallery — only for real (signed-in) users, and only once
            revealed if disposable mode is on. Guests only see it when the
            host has explicitly opted in. Couple always sees their own. */}
        {isRealUser && revealed && event && (() => {
          const isCouple = event.data.coupleUid === user?.uid;
          const guestsAllowed = event.data.galleryVisibleToGuests === true;
          if (!isCouple && !guestsAllowed) {
            return (
              <section className="text-center pt-6 pb-4">
                <p className="text-sm text-stone-400 italic">
                  The host has turned off the shared guest gallery.
                </p>
              </section>
            );
          }
          return <EventGallery eventId={event.eventId} />;
        })()}

        {/* Reveal hidden */}
        {!revealed && (
          <p className="text-center text-xs text-stone-400 leading-relaxed pt-2">
            The host's gallery is hidden until the big reveal — thanks for keeping the secret.
          </p>
        )}
      </main>

      {/* Footer */}
      <footer className="pb-8 text-center text-xs text-stone-300">
        <Link to="/" className="hover:text-stone-500 transition">Powered by KeptShots</Link>
      </footer>

      {nameEditorOpen && (
        <NameEditorModal
          initial={nickname}
          onCancel={() => setNameEditorOpen(false)}
          onSave={(next) => {
            setNickname(next);
            setNameEditorOpen(false);
          }}
        />
      )}

      {toast && (
        <UploadToast
          key={toast.id}
          name={effectiveName}
          count={toast.count}
          eventTitle={title}
          milestone={toast.milestone}
          primary={primary}
          dark={bgIsDark}
        />
      )}

    </div>
  );
}

/**
 * Small ephemeral confirmation shown after each successful upload. Positioned
 * bottom-center on mobile, bottom-right on wider screens so it doesn't cover
 * the upload zone. Non-interactive — dismisses itself.
 */
function UploadToast({
  name,
  count,
  eventTitle,
  milestone,
  primary,
  dark,
}: {
  name: string;
  count: number;
  eventTitle: string;
  milestone: boolean;
  primary: string;
  dark: boolean;
}) {
  return (
    <div className="fixed bottom-6 inset-x-4 sm:inset-x-auto sm:right-6 sm:left-auto z-40 pointer-events-none flex justify-center sm:justify-end animate-pop-in">
      <div
        className={`rounded-2xl px-4 py-3 shadow-2xl max-w-sm w-full sm:w-auto flex items-center gap-3 ${
          dark ? 'bg-white/10 backdrop-blur border border-white/10' : 'bg-white'
        }`}
        style={{
          borderLeft: milestone ? `4px solid ${primary}` : undefined,
        }}
      >
        <div
          className="w-8 h-8 rounded-full grid place-items-center shrink-0"
          style={{ backgroundColor: `${primary}22`, color: primary }}
          aria-hidden
        >
          {milestone ? '🎉' : '✓'}
        </div>
        <div className="min-w-0">
          <p className={`text-sm font-medium truncate ${dark ? 'text-white' : 'text-stone-800'}`}>
            {milestone ? `${count} photos in!` : `Thanks, ${name}!`}
          </p>
          <p className={`text-xs truncate ${dark ? 'text-white/70' : 'text-stone-500'}`}>
            {milestone
              ? `You're crushing it. Keep them coming.`
              : `Your photo is in ${eventTitle}'s collection.`}
          </p>
        </div>
      </div>
    </div>
  );
}

function NameEditorModal({
  initial,
  onCancel,
  onSave,
}: {
  initial: string;
  onCancel: () => void;
  onSave: (next: string) => void;
}) {
  const [value, setValue] = useState(initial);
  const trimmed = value.trim().slice(0, 40);
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-stone-950/70 flex items-center justify-center px-4 animate-fade-in"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl p-6 sm:p-7 w-full max-w-sm space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-1">
          <h3 className="font-serif text-2xl text-stone-900">Your name</h3>
          <p className="text-sm text-stone-500">
            Shown on your uploads so the host knows they're yours.
          </p>
        </div>
        <input
          autoFocus
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && trimmed) onSave(trimmed);
            if (e.key === 'Escape') onCancel();
          }}
          className="w-full border-b-2 border-stone-100 focus:border-stone-400 bg-transparent focus:outline-none py-2.5 text-lg"
          placeholder="Type your name"
          maxLength={40}
        />
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-full text-sm text-stone-500 hover:text-stone-800 transition"
          >
            Cancel
          </button>
          <button
            onClick={() => trimmed && onSave(trimmed)}
            disabled={!trimmed}
            className="px-4 py-2 rounded-full text-sm bg-stone-900 text-cream hover:bg-stone-800 transition disabled:opacity-40"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// -------- Gallery of every completed upload for the event --------

interface UploadRow {
  id: string;
  uploaderName?: string;
  createdAt?: unknown;
  hiddenAt?: unknown;
  status?: string;
}

function EventGallery({ eventId }: { eventId: string }) {
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [urls, setUrls] = useState<Record<string, string | null>>({});
  const [denied, setDenied] = useState<string | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, `events/${eventId}/uploads`),
      orderBy('createdAt', 'desc'),
      limit(60)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next: UploadRow[] = [];
        snap.forEach((d) => next.push({ id: d.id, ...(d.data() as Omit<UploadRow, 'id'>) }));
        setRows(next.filter((r) => r.status === 'complete' && !r.hiddenAt));
        setDenied(null);
      },
      (err) => {
        // If the host has the gallery turned off (or we're not a guest),
        // Firestore rules reject the read — treat that as "hidden".
        if (err.code === 'permission-denied') {
          setDenied('The host has turned off the shared guest gallery.');
        } else {
          setDenied(err.message);
        }
        setRows([]);
      }
    );
    return unsub;
  }, [eventId]);

  useEffect(() => {
    if (rows.length === 0) return;
    const ids = rows.map((r) => r.id);
    const call = httpsCallable<{ eventId: string; ids: string[] }, { urls: Record<string, string | null> }>(
      functions,
      'getGalleryThumbs'
    );
    call({ eventId, ids })
      .then((r) => setUrls(r.data.urls))
      .catch(() => setUrls({}));
  }, [eventId, rows]);

  if (denied) {
    return (
      <section className="text-center pt-6 pb-4">
        <p className="text-sm text-stone-400 italic">{denied}</p>
      </section>
    );
  }

  if (rows.length === 0) {
    return (
      <section className="text-center pt-6 pb-4">
        <p className="text-sm text-stone-400">
          No photos in the gallery yet — you're one of the first.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-3 pt-4">
      <div className="flex items-baseline justify-between px-1">
        <h2 className="font-serif text-2xl text-stone-800">Everyone's photos</h2>
        <p className="text-xs text-stone-400">{rows.length} shown</p>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        {rows.map((r) => (
          <GalleryTile key={r.id} row={r} url={urls[r.id] ?? null} />
        ))}
      </div>
    </section>
  );
}

function GalleryTile({ row, url }: { row: UploadRow; url: string | null }) {
  return (
    <div className="aspect-square rounded-lg overflow-hidden bg-stone-100 relative group">
      {url ? (
        <img
          src={url}
          alt={row.uploaderName ?? 'Guest photo'}
          loading="lazy"
          className="w-full h-full object-cover transition group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full skeleton" />
      )}
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.85 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.67-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.67 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

async function retryItem(id: string) {
  const { updateItem } = await import('../lib/queue');
  await updateItem(id, { status: 'pending', attempts: 0, lastError: undefined });
  const { startUploadWorker } = await import('../lib/uploadWorker');
  startUploadWorker();
}

function UploadTile({ item, accent }: { item: QueueItem; accent: string }) {
  const percent =
    item.status === 'complete'
      ? 100
      : item.bytesSent && item.size
      ? Math.max(0, Math.min(100, Math.round((item.bytesSent / item.size) * 100)))
      : 0;

  const statusLabel = (() => {
    switch (item.status) {
      case 'complete':
        return 'Shared';
      case 'uploading':
        return item.bytesSent ? `Sharing · ${percent}%` : 'Sharing…';
      case 'failed':
        return `Failed${item.lastError ? ' · ' + item.lastError.slice(0, 40) : ''}`;
      case 'pending':
        if (!navigator.onLine) return 'Waiting for signal';
        if (item.attempts > 0 && item.lastError) {
          return `Retrying · ${item.lastError.slice(0, 40)}`;
        }
        return 'Waiting to share…';
    }
  })();

  return (
    <li className="flex items-center gap-3 p-3 bg-white rounded-2xl shadow-card">
      <div className="relative w-14 h-14 shrink-0 rounded-xl overflow-hidden bg-stone-100">
        <img
          src={URL.createObjectURL(item.thumbBlob)}
          className="w-full h-full object-cover"
          alt=""
        />
        {item.status === 'complete' && (
          <div
            className="absolute inset-0 grid place-items-center bg-black/40 text-white"
            aria-hidden="true"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="truncate text-sm font-medium text-stone-700">{item.filename}</div>
        <div className={`text-xs mt-0.5 ${
          item.status === 'failed' || (item.status === 'pending' && item.attempts > 0)
            ? 'text-red-600'
            : 'text-stone-400'
        }`}>
          {statusLabel}
        </div>
        {item.status === 'uploading' && (
          <div className="w-full h-1 rounded-full bg-stone-100 mt-2 overflow-hidden">
            <div
              className="h-full rounded-full transition-[width]"
              style={{ width: `${percent}%`, background: accent }}
            />
          </div>
        )}
      </div>
      {item.status === 'failed' && (
        <button
          type="button"
          onClick={() => void retryItem(item.id)}
          className="text-xs font-medium px-3 py-1.5 rounded-full text-white shrink-0"
          style={{ background: accent }}
        >
          Retry
        </button>
      )}
    </li>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-full canvas-warm flex items-center justify-center">
      <div className="space-y-3 text-center">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-clay rounded-full animate-spin mx-auto" />
        <p className="text-sm text-stone-400">Loading…</p>
      </div>
    </div>
  );
}

/**
 * Live counter. Renders a subtle pill showing "🎉 N photos shared so far"
 * with a soft pulse whenever the count ticks up. Hidden below 1 so it never
 * shows a lonely zero.
 */
function LiveCounter({ count, primary }: { count: number; primary: string }) {
  const [prev, setPrev] = useState(count);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (count > prev) {
      setFlash(true);
      const t = window.setTimeout(() => setFlash(false), 900);
      return () => window.clearTimeout(t);
    }
    setPrev(count);
  }, [count, prev]);

  // Sync `prev` after the flash animation so subsequent ticks read cleanly.
  useEffect(() => {
    if (!flash) setPrev(count);
  }, [flash, count]);

  if (count < 1) return null;

  // Copy escalates with milestones so returning guests still notice progress.
  const label = (() => {
    if (count === 1) return 'photo shared so far';
    if (count < 10) return 'photos shared so far';
    if (count < 25) return 'photos in — keep them coming';
    if (count < 50) return "photos and counting";
    if (count < 100) return 'photos — this event is buzzing';
    return 'photos captured together';
  })();

  return (
    <div className="flex justify-center">
      <div
        className={`inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-stone-200/70 shadow-card transition-all ${
          flash ? 'scale-105' : ''
        }`}
        style={flash ? { borderColor: primary, boxShadow: `0 0 0 3px ${primary}22` } : {}}
      >
        <span
          className={`inline-block w-2 h-2 rounded-full ${flash ? 'animate-ping' : ''}`}
          style={{ background: primary }}
        />
        <span className="text-sm text-stone-700 tabular-nums">
          <span className="font-medium" style={{ color: primary }}>
            {count.toLocaleString()}
          </span>
          <span className="text-stone-500"> {label}</span>
        </span>
      </div>
    </div>
  );
}

function PendingScreen({ title }: { title?: string }) {
  return (
    <div className="min-h-full canvas-warm flex items-center justify-center p-6">
      <div className="max-w-sm text-center space-y-4 bg-white rounded-3xl shadow-card p-10 animate-fade-in">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-50 text-amber-600 grid place-items-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4l3 2" />
          </svg>
        </div>
        <div className="space-y-2">
          <h1 className="font-serif text-2xl">Almost ready.</h1>
          <p className="text-stone-500 text-sm leading-relaxed">
            {title ? <><span className="font-medium text-stone-800">{title}</span> is </> : 'This event is '}
            still being set up by the host. Check back in a bit — you'll be able to upload photos the moment it goes live.
          </p>
        </div>
        <Link to="/" className="inline-block text-sm text-stone-500 hover:text-stone-700 pt-2">
          Powered by KeptShots
        </Link>
      </div>
    </div>
  );
}

function PausedScreen({ title }: { title?: string }) {
  return (
    <div className="min-h-full canvas-warm flex items-center justify-center p-6">
      <div className="max-w-sm text-center space-y-4 bg-white rounded-3xl shadow-card p-10 animate-fade-in">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-stone-100 text-stone-500 grid place-items-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        </div>
        <div className="space-y-2">
          <h1 className="font-serif text-2xl">Paused for now.</h1>
          <p className="text-stone-500 text-sm leading-relaxed">
            {title ? <><span className="font-medium text-stone-800">{title}</span> is </> : 'This event is '}
            temporarily paused by the host. Give it a bit and try again — your photos will queue as soon as it's back on.
          </p>
        </div>
        <Link to="/" className="inline-block text-sm text-stone-500 hover:text-stone-700 pt-2">
          Powered by KeptShots
        </Link>
      </div>
    </div>
  );
}

function ErrorScreen({ msg }: { msg: string }) {
  return (
    <div className="min-h-full canvas-warm flex items-center justify-center p-6">
      <div className="max-w-sm text-center space-y-4 bg-white rounded-3xl shadow-card p-10 animate-fade-in">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-red-50 text-red-500 grid place-items-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
        </div>
        <div className="space-y-2">
          <h1 className="font-serif text-2xl">Hmm.</h1>
          <p className="text-stone-500 text-sm leading-relaxed">{msg}</p>
        </div>
        <Link to="/" className="inline-block text-sm text-stone-500 hover:text-stone-700 pt-2">
          Powered by KeptShots
        </Link>
      </div>
    </div>
  );
}

function CameraIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="3"/>
      <circle cx="12" cy="12" r="3.5"/>
      <path d="M9 3h6l1 2H8z"/>
    </svg>
  );
}

function Spinner() {
  return (
    <div className="w-6 h-6 border-2 border-current/30 border-t-current rounded-full animate-spin" />
  );
}

function BackArrowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-3.5-7.1" />
      <path d="M21 4v5h-5" />
    </svg>
  );
}
